import type {
  IWorkOrderContainersFilters,
  IWorkOrderDashboardMinersFilters,
  IWorkOrderFilters,
} from '@/types/rc/filters'
import type { IWorkOrderChatMessageForm, IWorkOrderUpdateForm } from '@/types/rc/forms'
import type { IWorkOrderShipment } from '@/types/rc/shipments'
import type { IFilters, IFragmentFilters } from '@/types/shared/filters'
import type {
  IWorkOrderDetails,
  IWorkOrderDashboardShipment,
  WorkOrderDashboardSummary,
  IListWorkOrder,
  IWorkOrderDashboardMiner,
  IWorkOrderChatMessage,
  IWorkOrderContainer,
} from '@/types/rc/work-orders'
import type { IPage } from '@/types/shared/pagination'
import axios from 'axios'

/**
 * Gets all work orders with optional filters.
 *
 * @param filters work orders endpoint filters.
 * @returns work orders dashboard data.
 * @throws {AxiosError}
 */
export async function getWorkOrders(filters?: IWorkOrderFilters): Promise<IPage<IListWorkOrder>> {
  const res = await axios.get(import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT, {
    params: filters,
  })
  return res.data
}

/**
 * Gets work order dashboard summary
 *
 * @param number work order number.
 * @returns work orders dashboard summary data.
 * @throws {AxiosError}
 */
export async function getWorkOrderDashboardSummary(number: string): Promise<WorkOrderDashboardSummary> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${number}/dashboard/summary`)
  return res.data
}

/**
 * Gets paginated miners for work order dashboard
 *
 * @param {IWorkOrderDashboardMinersFilters} filters
 * @returns paginated miners data.
 */
export async function getWorkOrderDashboardMiners(
  number: string,
  filters: IWorkOrderDashboardMinersFilters
): Promise<IPage<IWorkOrderDashboardMiner>> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${number}/dashboard/miners`, {
    params: filters,
  })
  return res.data
}

/**
 * Gets all work order shipments
 *
 * @returns {Promise<IWorkOrderShipment[]>} an array of work order shipments.
 * @throws {AxiosError}
 */

export const getAllWorkOrderShipments = async (number: string): Promise<IWorkOrderShipment[]> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${number}/shipments`)
  return res.data
}

/**
 * Retrieves the Miners CSV associated with the work order
 *
 * @param number work order number.
 */
export async function getWorkOrderMinersExportCSV(number: string): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${number}/miners/export`, {
    responseType: 'blob',
  })
  return res.data
}

/**
 * Updates a work order
 *
 * @param string work order number.
 * @param form update work order form.
 * @returns {Promise<any>} updated work order.
 * @throws {AxiosError}
 */
export async function updateWorkOrder(workOrderNumber: string, form: IWorkOrderUpdateForm): Promise<IWorkOrderDetails> {
  const res = await axios.patch(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${workOrderNumber}`, form)
  return res.data
}

/**
 * Get work order details
 *
 * @returns {Promise<IWorkOrderDetails>} work order.
 * @throws {AxiosError}
 */
export const getWorkOrderDetails = async (workOrderNumber: string): Promise<IWorkOrderDetails> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${workOrderNumber}`)
  return res.data
}

/**
 * Get work order shipments
 *
 * @returns {Promise<IWorkOrderDashboardShipment[]>} an array of work order dashboard shipments.
 */
export const getWorkOrderDashboardShipments = async (
  workOrderNumber: string,
  filters: IFragmentFilters
): Promise<IWorkOrderDashboardShipment[]> => {
  const res = await axios.get(
    `${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${workOrderNumber}/dashboard/shipments`,
    { params: filters }
  )
  return res.data
}

/**
 * Retrieves the Spare Part CSV associated with the provided filters
 *
 * @param filters spare part filters.
 */
export async function getWorkOrdersExportCSV(filters: IWorkOrderFilters): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/export`, {
    params: filters,
    responseType: 'blob',
  })
  return res.data
}

/**
 * Move a list of miners to a work order
 *
 * @param number work order number.
 * @param minerIDs the list of miner IDs to add.
 */
export async function moveMinersToWorkOrder(number: string, minerIDs: number[]) {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${number}/miners`, {
    miner_ids: minerIDs,
  })
  return res.data
}

export async function sendWorkOrderChatMessage(
  workOrderNumber: string,
  form: IWorkOrderChatMessageForm
): Promise<void> {
  const res = await axios.post(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${workOrderNumber}/chat`, form)
  return res.data
}

export const getWorkOrderChatMessages = async (
  workOrderNumber: string,
  filters: IFilters
): Promise<IPage<IWorkOrderChatMessage>> => {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${workOrderNumber}/chat`, {
    params: filters,
  })
  return res.data
}

export async function getWorkOrderDashboardMinersExportCSV(
  number: string,
  filters: IWorkOrderDashboardMinersFilters
): Promise<Blob> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${number}/dashboard/miners/export`, {
    params: filters,
    responseType: 'blob',
  })
  return res.data
}

export async function getWorkOrderContainers(
  workOrderNumber: string,
  filters: IWorkOrderContainersFilters
): Promise<IWorkOrderContainer[]> {
  const res = await axios.get(`${import.meta.env.VITE_APP_RC_WORK_ORDERS_ENDPOINT}/${workOrderNumber}/containers`, {
    params: filters,
  })
  return res.data
}
